/**
 * One confirmation dialog for every change that removes, revokes, archives or otherwise takes
 * something away. It names the thing and what will happen, runs the dry run as it opens so a
 * refused change says why before anything can be confirmed, and, for the few changes that cannot
 * be undone, asks for the name to be typed.
 *
 *   if (await confirmAction({ title: "Archive project 'X'?", body: "…", action: "Archive", check: () => dryRun() })) act();
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ErrorBox } from "./ui";

export interface Confirm {
  /** a question naming the object: "Archive project 'Client A'?" */
  title: string;
  /** what happens, in a sentence or two */
  body?: ReactNode;
  /** the button: "Archive", "Revoke", "Delete" */
  action: string;
  /** red button (default); false for changes that are disruptive but not destructive */
  danger?: boolean;
  /** for changes that cannot be undone: the text to type before the button works */
  typed?: string;
  /** the dry run; a rejection is shown in the dialog and the change cannot be confirmed */
  check?: () => Promise<unknown>;
  /** ask for a value instead: the field's label and starting value (askText) */
  input?: { label: string; value: string; type?: string; help?: string; required?: boolean };
}

type Pending = Confirm & { resolve: (ok: boolean) => void; got?: (v: string) => void };
let open: ((p: Pending) => void) | undefined;

/** Ask; resolves true only when the person confirms. */
export function confirmAction(c: Confirm): Promise<boolean> {
  return new Promise((resolve) => {
    if (open) open({ ...c, resolve });
    else resolve(window.confirm(c.title)); // no host mounted (should not happen): still ask
  });
}

/** Ask for one value (a new name, a URL); resolves undefined if cancelled or unchanged. */
export function askText(c: Omit<Confirm, "input" | "typed"> & { input: NonNullable<Confirm["input"]> }): Promise<string | undefined> {
  return new Promise((resolve) => {
    let value: string | undefined;
    const p = { ...c, danger: c.danger ?? false, resolve: (ok: boolean) => resolve(ok && value !== undefined && value !== c.input.value ? value : undefined), got: (v: string) => (value = v) };
    if (open) open(p);
    else resolve(window.prompt(c.title, c.input.value) ?? undefined);
  });
}

/** Mounted once, at the top of the app. */
export function ConfirmHost() {
  const [p, setP] = useState<Pending>();
  const [check, setCheck] = useState<"checking" | "ok" | { error: unknown }>("ok");
  const [typed, setTyped] = useState("");
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    open = (next) => {
      setTyped(next.input?.value ?? "");
      setCheck(next.check ? "checking" : "ok");
      setP(next);
      next.check?.().then(
        () => setCheck("ok"),
        (error: unknown) => setCheck({ error }),
      );
    };
    return () => {
      open = undefined;
    };
  }, []);

  useEffect(() => {
    const d = ref.current;
    if (p && d && !d.open) d.showModal();
  }, [p]);

  const finish = (ok: boolean) => {
    p?.resolve(ok);
    setP(undefined);
    ref.current?.close();
  };

  const ready = check === "ok" && (!p?.typed || typed.trim() === p.typed) && (!p?.input || p.input.required === false || typed.trim() !== "");
  return (
    <dialog ref={ref} className="confirm" aria-labelledby="confirm-title" onCancel={(e) => (e.preventDefault(), finish(false))}>
      {p && (
        <form method="dialog" autoComplete="off" onSubmit={(e) => (e.preventDefault(), ready && (p.got?.(typed.trim()), finish(true)))}>
          <h2 id="confirm-title">{p.title}</h2>
          {p.body && <div className="confirm-body">{p.body}</div>}
          {check === "checking" && <p className="muted">Checking the change…</p>}
          {typeof check === "object" && <ErrorBox error={check.error} />}
          {p.input && (
            <div className="field">
              <label>
                <span className="field-label">{p.input.label}</span>
                <input type={p.input.type ?? "text"} value={typed} autoFocus spellCheck={false} onChange={(e) => setTyped(e.target.value)} />
              </label>
              {p.input.help && <small className="field-help">{p.input.help}</small>}
            </div>
          )}
          {p.typed && check === "ok" && (
            <label className="confirm-typed">
              This cannot be undone. Type <code>{p.typed}</code> to confirm.
              <input value={typed} autoFocus autoComplete="off" spellCheck={false} onChange={(e) => setTyped(e.target.value)} />
            </label>
          )}
          <div className="confirm-actions">
            <button type="button" className="secondary" onClick={() => finish(false)} autoFocus={!p.typed && !p.input}>
              {typeof check === "object" ? "Close" : "Cancel"}
            </button>
            {typeof check !== "object" && (
              <button type="submit" className={p.danger === false ? "" : "danger"} disabled={!ready}>
                {p.action}
              </button>
            )}
          </div>
        </form>
      )}
    </dialog>
  );
}

/**
 * Confirm a change whose dry run has already passed, from a sentence describing what it does
 * ("Remove type 'Spike' with its states. Objects keep…"): the first sentence becomes the question,
 * its first word the button, and the rest the body.
 */
export function confirmImpact(impact: string): Promise<boolean> {
  const m = /^(.+?[.!?])(\s+[\s\S]*)?$/.exec(impact.trim());
  const first = (m?.[1] ?? impact).replace(/[.!]$/, "");
  const rest = m?.[2]?.trim();
  const action = /^\S+/.exec(first)?.[0] ?? "Go ahead";
  return confirmAction({
    title: first.endsWith("?") ? first : `${first}?`,
    body: (
      <>
        {rest && <p>{rest}</p>}
        <p className="muted">Checked: the change is allowed.</p>
      </>
    ),
    action,
  });
}
